import { useState, useEffect, useCallback, useRef } from "react";
import * as db from "../lib/db";
import * as ai from "../lib/ai";
import { genId, todayKey, dateKey } from "../utils/helpers";
import { enqueue, getPendingCount } from "../utils/offline-queue";
import { hapticMedium, hapticStrong, hapticSuccess } from "../utils/haptics";
import { useToast } from "./useToast";
import { syncWorkoutToHealth } from "../utils/healthkit";
import { track } from "../lib/analytics";

// Shared helper: map AI exercise list to exercise blocks, creating custom
// exercises for any names that don't match the library.
import { prescriptionFor } from "../utils/mood";

/**
 * How big today's session is allowed to be, given how the person said they are.
 *
 * Never longer than the profile asks for, never longer than the day can carry:
 * an awful, drained morning gets ten minutes however long the profile says a
 * session is. Without a check-in it is just the profile.
 */
export function sessionSizing(profileMinutes, checkin) {
  const p =
    checkin?.mood != null && checkin?.energy != null
      ? prescriptionFor(checkin.mood, checkin.energy)
      : null;
  return {
    available_time: p ? Math.min(profileMinutes, p.minutes) : profileMinutes,
    energy_level: checkin?.energy ?? 3,
    mood: checkin?.mood ?? undefined,
    session_type: p?.key,
  };
}

export function buildWorkoutBlocks(aiExercises, exercises, onNewExercises) {
  const newExercises = [];

  const blocks = (aiExercises || []).map((aiEx) => {
    const aiName = (aiEx.name || "").trim().toLowerCase();
    // Exact (case-insensitive) match first.
    let match = exercises.find((e) => e.name.toLowerCase() === aiName);
    // Fuzzy fallback ONLY when unambiguous — exactly one library exercise
    // contains/is-contained-by the AI name. A non-unique substring match would
    // silently log to the wrong lift (e.g. "Bench Press" → "Decline Bench Press").
    if (!match && aiName) {
      const fuzzy = exercises.filter(
        (e) =>
          aiName.includes(e.name.toLowerCase()) ||
          e.name.toLowerCase().includes(aiName)
      );
      if (fuzzy.length === 1) match = fuzzy[0];
    }

    if (!match) {
      // Sane defaults so the custom exercise renders (category chip) and uses
      // the correct unit-aware input layout instead of a blank/undefined one.
      match = { id: genId(), name: aiEx.name, category: "Other", muscle: "Other", unit: "lbs", custom: true };
      newExercises.push(match);
    }

    // Preserve the AI's prescription strings ("8-10", "AMRAP", "Bodyweight")
    // for display — they're lost once sets are coerced to numbers below.
    const targetReps = aiEx.reps != null ? String(aiEx.reps) : "";
    const targetWeight = aiEx.weight != null ? String(aiEx.weight) : "";

    const numericWeight = parseFloat(String(aiEx.weight || "").replace(/[^0-9.]/g, "")) || "";
    // Low end of a range ("8-10" → 8); AMRAP / non-numeric → blank.
    const numericReps = parseInt(aiEx.reps, 10) || "";

    const sets = [];
    for (let i = 0; i < (aiEx.sets || 3); i++) {
      sets.push({
        reps: numericReps === "" ? "" : String(numericReps),
        weight: numericWeight === "" ? "" : String(numericWeight),
        done: false,
      });
    }

    let restSeconds = 90;
    if (aiEx.rest) {
      const r = aiEx.rest.toLowerCase();
      const mins = r.match(/(\d+)\s*min/);
      const secs = r.match(/(\d+)\s*sec/);
      restSeconds = 0;
      if (mins) restSeconds += parseInt(mins[1]) * 60;
      if (secs) restSeconds += parseInt(secs[1]);
      if (restSeconds === 0) restSeconds = 90;
    }

    return { exerciseId: match.id, sets, restSeconds, targetReps, targetWeight };
  });

  if (newExercises.length > 0) onNewExercises(newExercises);
  return blocks;
}

// "30-45 min" → 45, "60+ min" → 60, "20-30 min" → 30. Default 45.
function parseSessionMinutes(sessionDuration) {
  const nums = String(sessionDuration || "").match(/\d+/g);
  if (!nums || nums.length === 0) return 45;
  return parseInt(nums[nums.length - 1], 10) || 45;
}

export function useWorkoutActions({
  userId,
  workouts,
  exercises,
  personalRecords,
  todayAiWorkout,
  runs,
  onboardingCompleted,
  isOnline,
  loading,
  userProfile,
  onSaveMood,
  todayCheckin,
  setTodayAiWorkout,
  setWorkouts,
  setExercises,
  setPersonalRecords,
  setPendingSyncs,
}) {
  const toast = useToast();

  const [activeWorkout, setActiveWorkout] = useState(null);
  const [sessionPrs, setSessionPrs] = useState([]);

  // ── Mid-workout persistence ──
  // The active session lives in React state, so a backgrounded app getting
  // killed by iOS (guaranteed during a 45-min gym session) lost every logged
  // set. Mirror it to localStorage on every change and restore on launch.
  const ACTIVE_WORKOUT_KEY = userId ? `active_workout_${userId}` : null;
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!ACTIVE_WORKOUT_KEY || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = localStorage.getItem(ACTIVE_WORKOUT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      // Only resume sessions started in the last 12 hours
      if (saved?.workout?.startTime && Date.now() - saved.workout.startTime < 12 * 3600000) {
        setActiveWorkout(saved.workout);
        if (Array.isArray(saved.sessionPrs)) setSessionPrs(saved.sessionPrs);
      } else {
        localStorage.removeItem(ACTIVE_WORKOUT_KEY);
      }
    } catch { /* corrupted snapshot — ignore */ }
  }, [ACTIVE_WORKOUT_KEY]);

  useEffect(() => {
    if (!ACTIVE_WORKOUT_KEY) return;
    try {
      if (activeWorkout) {
        localStorage.setItem(ACTIVE_WORKOUT_KEY, JSON.stringify({ workout: activeWorkout, sessionPrs }));
      } else {
        localStorage.removeItem(ACTIVE_WORKOUT_KEY);
      }
    } catch { /* storage full — never break the workout */ }
  }, [ACTIVE_WORKOUT_KEY, activeWorkout, sessionPrs]);

  const [aiWorkoutLoading, setAiWorkoutLoading] = useState(false);
  const [aiWorkoutError, setAiWorkoutError] = useState(null);
  const [workoutInsight, setWorkoutInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [postWorkoutData, setPostWorkoutData] = useState(null);

  const autoGenAttempted = useRef(false);

  const handleGenerateWorkout = useCallback(async (context, { silent = false } = {}) => {
    if (!userId) {
      setAiWorkoutError("Not signed in. Please sign out and back in.");
      return null;
    }
    setAiWorkoutLoading(true);
    setAiWorkoutError(null);
    try {
      const result = await ai.generateWorkout(userId, context);
      if (!result?.workout) throw new Error("No workout returned. Try again.");
      const generated = {
        id: result.generated_workout_id,
        workout_json: result.workout,
        created_at: new Date().toISOString(),
      };
      // Display it directly — callers (including the auto-generate effect)
      // previously had to remember to set this themselves, and the auto-gen
      // path didn't, so its workouts were generated but never shown.
      setTodayAiWorkout?.(generated);
      track("workout_generated", { focus: context?.muscle_focus, time: context?.available_time });
      return generated;
    } catch (e) {
      console.error("Failed to generate workout:", e);
      setAiWorkoutError(e.message || "Failed to generate workout. Try again.");
      // A background generation the user never asked for does not get to
      // interrupt them. The card still shows the error and a retry.
      if (!silent) toast.error("Failed to generate workout");
      return null;
    } finally {
      setAiWorkoutLoading(false);
    }
  }, [userId, toast, setTodayAiWorkout]);

  // Smart defaults for one-tap generation: session length from the user's
  // profile, focus from muscle recency — the same signals the form collects,
  // derived automatically so generating requires zero questions.
  const buildSmartContext = useCallback(() => {
    const twoDaysAgo = Date.now() - 2 * 86400000;
    const recentMuscles = new Set();
    for (const w of workouts.filter((w) => w.startTime >= twoDaysAgo)) {
      for (const b of w.exerciseBlocks || []) {
        const ex = exercises.find((e) => e.id === b.exerciseId);
        if (ex?.category) recentMuscles.add(ex.category);
      }
    }

    const muscleGroups = ["Chest", "Back", "Legs", "Shoulders", "Arms", "Core"];
    const muscleRecency = {};
    for (const cat of muscleGroups) {
      for (const w of [...workouts].sort((a, b) => b.startTime - a.startTime)) {
        const hasCat = (w.exerciseBlocks || []).some((b) => {
          const ex = exercises.find((e) => e.id === b.exerciseId);
          return ex && ex.category === cat;
        });
        if (hasCat) {
          muscleRecency[cat] = Math.floor((Date.now() - w.startTime) / 86400000);
          break;
        }
      }
    }

    const suggestedFocus =
      muscleGroups
        .filter((c) => !recentMuscles.has(c))
        .sort((a, b) => (muscleRecency[b] ?? 999) - (muscleRecency[a] ?? 999))[0] ||
      muscleGroups.find((c) => !recentMuscles.has(c)) ||
      "Chest";

    // Sized to how they said they are. Without a check-in this falls back to
    // the profile defaults; with one, the session never runs longer than the
    // day can carry — a ten-minute walk on an awful, drained morning.
    return {
      ...sessionSizing(parseSessionMinutes(userProfile?.sessionDuration), todayCheckin),
      muscle_focus: suggestedFocus,
      recent_muscles: [...recentMuscles],
    };
  }, [workouts, exercises, userProfile, todayCheckin]);

  // One-tap generate/regenerate — no form, smart defaults, optional overrides.
  const handleQuickGenerate = useCallback((overrides = {}) => {
    setTodayAiWorkout?.(null);
    return handleGenerateWorkout({ ...buildSmartContext(), ...overrides });
  }, [buildSmartContext, handleGenerateWorkout, setTodayAiWorkout]);

  // Auto-generate today's session once the person has said how they are.
  // It used to fire on app open, before the check-in, so every session was
  // built blind — and if that background request failed, the very first thing
  // on screen was an error toast for a job nobody asked for.
  useEffect(() => {
    if (!userId || loading || aiWorkoutLoading || todayAiWorkout) return;
    if (!onboardingCompleted || !isOnline) return;
    if (!todayCheckin) return;
    if (autoGenAttempted.current) return;

    const today = todayKey();
    const trainedToday =
      workouts.some((w) => dateKey(w.startTime) === today) ||
      (runs || []).some((r) => dateKey(r.timestamp) === today);
    if (trainedToday) return;

    autoGenAttempted.current = true;
    handleGenerateWorkout(buildSmartContext(), { silent: true });
  }, [userId, loading, aiWorkoutLoading, todayAiWorkout, onboardingCompleted, isOnline, workouts, runs, buildSmartContext, handleGenerateWorkout, todayCheckin]);

  const handleBeginAiWorkout = useCallback((aiWorkoutData) => {
    const aiW = aiWorkoutData?.workout_json;
    if (!aiW) return;

    const blocks = buildWorkoutBlocks(aiW.exercises, exercises, (newExs) => {
      setExercises((prev) => [...prev, ...newExs]);
      for (const ex of newExs) {
        db.insertUserExercise(userId, ex).catch((e) => {
          console.error(`Failed to persist AI exercise "${ex.name}":`, e);
        });
      }
    });

    const workout = {
      id: genId(),
      name: aiW.workout_name || "AI Workout",
      startTime: Date.now(),
      exerciseBlocks: blocks,
      notes: "",
      generatedWorkoutId: aiWorkoutData.id,
    };

    setSessionPrs([]);
    setActiveWorkout(workout);

    if (aiWorkoutData.id) {
      ai.acceptGeneratedWorkout(aiWorkoutData.id).catch((e) => console.error("Failed to mark AI workout accepted:", e));
    }
  }, [exercises, userId, setExercises]);

  const handleBeginChatWorkout = useCallback((aiWorkout) => {
    const blocks = buildWorkoutBlocks(aiWorkout.exercises, exercises, (newExs) => {
      setExercises((prev) => [...prev, ...newExs]);
      for (const ex of newExs) {
        db.insertUserExercise(userId, ex).catch((e) => {
          console.error(`Failed to persist chat exercise "${ex.name}":`, e);
        });
      }
    });

    const workout = {
      id: genId(),
      name: aiWorkout.name || "Coach Workout",
      startTime: Date.now(),
      exerciseBlocks: blocks,
      notes: "",
    };
    setSessionPrs([]);
    setActiveWorkout(workout);
  }, [exercises, userId, setExercises]);

  const checkAndSetPR = useCallback(async (exerciseId, exerciseName, weight, reps, onPR) => {
    const w = parseFloat(weight) || 0;
    if (w <= 0) return false;
    const cur = personalRecords[exerciseId];
    if (!cur || w > cur.weight) {
      const record = { weight: w, reps: parseFloat(reps), date: Date.now() };
      setPersonalRecords((prev) => ({ ...prev, [exerciseId]: record }));
      setSessionPrs((prev) => [...prev, { exerciseName, weight: w, reps: parseFloat(reps) }]);
      hapticStrong();
      track("pr_set", { exercise: exerciseName, weight: w });
      if (onPR) onPR({ exerciseId, exerciseName, weight: w, reps: parseFloat(reps), previousWeight: cur?.weight || null });

      try {
        await db.upsertPersonalRecord(userId, exerciseId, record);
      } catch (e) {
        console.error("Failed to save PR:", e);
        toast.error("Failed to save PR");
      }
      return true;
    }
    return false;
  }, [personalRecords, userId, setPersonalRecords, toast]);

  const finishWorkout = useCallback(async (w) => {
    const done = { ...w, endTime: Date.now() };
    setWorkouts((prev) => [done, ...prev]);
    setActiveWorkout(null);
    hapticSuccess();

    setPostWorkoutData({ workout: done, prs: [...sessionPrs] });
    setWorkoutInsight(null);
    setInsightLoading(true);
    track("workout_completed", {
      duration_min: Math.round((done.endTime - done.startTime) / 60000),
      prs: sessionPrs.length,
      ai_generated: !!done.generatedWorkoutId,
    });

    try {
      await db.insertWorkout(userId, done);
      toast.success("Workout saved");
      // Sync to HealthKit (iOS only, no-op on web)
      syncWorkoutToHealth(done).catch((e) => console.error("HealthKit workout sync failed:", e));
    } catch (e) {
      console.error("Failed to save workout:", e);
      if (!isOnline) {
        enqueue({ type: "workout", payload: { userId, workout: done } });
        setPendingSyncs(getPendingCount());
        toast.info("Workout saved offline — will sync when connected");
      } else {
        toast.error("Failed to save workout");
      }
    }

    if (userId) {
      const generatedW = done.generatedWorkoutId ? todayAiWorkout?.workout_json : null;
      ai.getPostWorkoutInsight(userId, done, generatedW, sessionPrs)
        .then((insight) => {
          setWorkoutInsight(insight);
          setInsightLoading(false);
        })
        .catch((e) => {
          console.warn("Failed to load post-workout insight:", e.message);
          setWorkoutInsight({
            fallback: true,
            headline: "Session complete.",
            insight: "Great work getting your training in today. We couldn't load your personalized insight right now.",
            next_focus: "Come back tomorrow and keep building.",
          });
          setInsightLoading(false);
        });
    } else {
      setInsightLoading(false);
    }
    setSessionPrs([]);
  }, [userId, workouts, sessionPrs, todayAiWorkout, isOnline, setWorkouts, setPendingSyncs, toast]);

  const dismissPostWorkout = useCallback(({ rpe, soreness, caloriesBurned, mood } = {}) => {
    // Post-session mood is the other half of the within-session delta the
    // Evidence Card is built on — link it to the workout so it can be paired.
    if (mood != null && postWorkoutData?.workout?.id) {
      onSaveMood?.({
        mood,
        context: "post_session",
        workoutId: postWorkoutData.workout.id,
      });
    }
    if ((rpe || soreness || caloriesBurned) && postWorkoutData?.workout?.id) {
      db.updateWorkoutFeedback(postWorkoutData.workout.id, { rpe, soreness, caloriesBurned }).catch((e) => console.error("Failed to save workout feedback:", e));
      if (caloriesBurned) {
        setWorkouts((prev) =>
          prev.map((w) =>
            w.id === postWorkoutData.workout.id ? { ...w, caloriesBurned, rpe } : w
          )
        );
      }
    }
    setPostWorkoutData(null);
    setWorkoutInsight(null);
    setInsightLoading(false);
  }, [postWorkoutData, setWorkouts, onSaveMood]);

  const handleDeleteWorkout = useCallback(async (id) => {
    hapticMedium();
    const deleted = workouts.find((w) => w.id === id);
    const remaining = workouts.filter((w) => w.id !== id);
    setWorkouts(remaining);

    if (deleted?.exerciseBlocks) {
      const affectedIds = [...new Set(deleted.exerciseBlocks.map((b) => b.exerciseId))];
      const updatedPRs = { ...personalRecords };

      for (const exId of affectedIds) {
        let bestWeight = 0, bestReps = 0, bestDate = 0;
        for (const w of remaining) {
          for (const b of w.exerciseBlocks || []) {
            if (b.exerciseId !== exId) continue;
            for (const s of b.sets || []) {
              const sw = parseFloat(s.weight) || 0;
              if (sw > bestWeight) {
                bestWeight = sw;
                bestReps = parseFloat(s.reps) || 0;
                bestDate = w.startTime;
              }
            }
          }
        }
        if (bestWeight > 0) {
          updatedPRs[exId] = { weight: bestWeight, reps: bestReps, date: bestDate };
        } else {
          delete updatedPRs[exId];
          try { await db.deletePersonalRecord(userId, exId); } catch { /* ignored */ }
        }
      }

      setPersonalRecords(updatedPRs);
      for (const exId of affectedIds) {
        if (updatedPRs[exId]) {
          try { await db.upsertPersonalRecord(userId, exId, updatedPRs[exId]); } catch { /* ignored */ }
        }
      }
    }

    try {
      await db.deleteWorkout(id);
    } catch (e) {
      console.error("Failed to delete workout:", e);
    }
  }, [userId, workouts, personalRecords, setWorkouts, setPersonalRecords]);

  const reset = useCallback(() => {
    setActiveWorkout(null);
    setSessionPrs([]);
    setAiWorkoutLoading(false);
    setAiWorkoutError(null);
    setWorkoutInsight(null);
    setInsightLoading(false);
    setPostWorkoutData(null);
    autoGenAttempted.current = false;
  }, []);

  return {
    activeWorkout, setActiveWorkout,
    sessionPrs,
    aiWorkoutLoading,
    aiWorkoutError, setAiWorkoutError: (v) => setAiWorkoutError(v),
    workoutInsight,
    insightLoading,
    postWorkoutData,
    handleGenerateWorkout,
    handleQuickGenerate,
    handleBeginAiWorkout,
    handleBeginChatWorkout,
    checkAndSetPR,
    finishWorkout,
    dismissPostWorkout,
    handleDeleteWorkout,
    reset,
  };
}
